import { initCarouselSwipe } from '../../utils/carousel-swipe';
import { equalizeHeights } from '../../utils/equalize-heights';

const ROOT_SELECTOR = '[data-product-tiers]';
const TAB_SELECTOR = '[data-product-tiers-tab]';
const PANEL_SELECTOR = '[data-product-tiers-panel]';
const VIEWPORT_SELECTOR = '[data-product-tiers-carousel]';
const TRACK_SELECTOR = '[data-product-tiers-track]';
const SLIDE_SELECTOR = '[data-product-tiers-slide]';
const PROGRESS_SELECTOR = '[data-product-tiers-progress]';
const PREV_SELECTOR = '[data-product-tiers-prev]';
const NEXT_SELECTOR = '[data-product-tiers-next]';
const NAV_SELECTOR = '[data-product-tiers-nav]';
const BLADE_SELECTOR = '[data-product-tiers-blade]';
const BLADE_OPEN_SELECTOR = '[data-product-tiers-blade-open]';
const BLADE_CLOSE_SELECTOR = '[data-product-tiers-blade-close]';
const BLADE_VIEW_SELECTOR = '[data-product-tiers-blade-view]';
const HEADER_SELECTOR = '.product-tiers__card-header';
const GRID_MQ = '(min-width: 1500px)';

function getVisibleCount(viewport: HTMLElement, slideWidth: number, gap: number): number {
  if (!slideWidth) return 1;
  return Math.max(1, Math.floor((viewport.offsetWidth + gap) / (slideWidth + gap)));
}

export function initProductTiers(): void {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach(initProductTiersRoot);
}

function initProductTiersRoot(root: HTMLElement): void {
  const tabs = root.querySelectorAll<HTMLButtonElement>(TAB_SELECTOR);
  const panels = root.querySelectorAll<HTMLElement>(PANEL_SELECTOR);

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const id = tab.getAttribute('data-product-tiers-tab');
      if (!id) return;

      tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('product-tiers__toggle-btn--active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-product-tiers-panel') === id;
        panel.classList.toggle('product-tiers__panel--hidden', !isActive);
        if (isActive) {
          equalizeCardHeaders(panel);
        }
      });
    });
  });

  panels.forEach((panel) => initProductTiersCarousel(panel));
  initProductTiersBlade(root);
}

function initProductTiersBlade(root: HTMLElement): void {
  const blade = root.querySelector<HTMLElement>(BLADE_SELECTOR);
  if (!blade) return;

  const views = blade.querySelectorAll<HTMLElement>(BLADE_VIEW_SELECTOR);
  const openButtons = root.querySelectorAll<HTMLButtonElement>(BLADE_OPEN_SELECTOR);
  const closeButtons = blade.querySelectorAll<HTMLButtonElement>(BLADE_CLOSE_SELECTOR);

  function setBladeOpen(isOpen: boolean, viewIndex?: string): void {
    document.body.classList.toggle('product-tiers-blade-open', isOpen);
    blade.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    if (isOpen && viewIndex !== undefined) {
      views.forEach((view) => {
        const isActive = view.getAttribute('data-product-tiers-blade-view') === viewIndex;
        view.classList.toggle('product-tiers__blade-view--hidden', !isActive);
      });
    }
  }

  openButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const index = button.getAttribute('data-product-tiers-blade-open');
      if (index === null) return;
      setBladeOpen(true, index);
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => setBladeOpen(false));
  });

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('product-tiers-blade-open')) {
      setBladeOpen(false);
    }
  });
}

function equalizeCardHeaders(panel: HTMLElement): void {
  if (panel.classList.contains('product-tiers__panel--hidden')) return;

  equalizeHeights(panel.querySelectorAll<HTMLElement>(HEADER_SELECTOR));
}

function initProductTiersCarousel(panel: HTMLElement): void {
  const viewport = panel.querySelector<HTMLElement>(VIEWPORT_SELECTOR);
  const track = panel.querySelector<HTMLElement>(TRACK_SELECTOR);
  const slides = panel.querySelectorAll<HTMLElement>(SLIDE_SELECTOR);
  const progress = panel.querySelector<HTMLElement>(PROGRESS_SELECTOR);
  const prev = panel.querySelector<HTMLButtonElement>(PREV_SELECTOR);
  const next = panel.querySelector<HTMLButtonElement>(NEXT_SELECTOR);
  const nav = panel.querySelector<HTMLElement>(NAV_SELECTOR);

  if (!viewport || !track || !slides.length) return;

  let index = 0;

  function isCarouselMode(): boolean {
    return !window.matchMedia(GRID_MQ).matches;
  }

  function getCarouselMetrics(): { gap: number; slideWidth: number; visible: number; maxIndex: number } {
    const slide = slides[0];
    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    const slideWidth = slide.offsetWidth;
    const visible = getVisibleCount(viewport, slideWidth, gap);
    const maxIndex = Math.max(0, slides.length - visible);

    return { gap, slideWidth, visible, maxIndex };
  }

  function getBaseOffset(): number {
    const { gap, slideWidth } = getCarouselMetrics();
    return index * (slideWidth + gap);
  }

  function applyTrackOffset(offsetPx: number): void {
    track.style.transform = `translateX(-${offsetPx}px)`;
  }

  function update(): void {
    if (isCarouselMode()) {
      const { gap, slideWidth, visible, maxIndex } = getCarouselMetrics();

      index = Math.min(index, maxIndex);
      applyTrackOffset(getBaseOffset());

      if (progress && slides.length > 1) {
        const progressEnd = Math.min(slides.length, index + visible);
        progress.style.width = `${(progressEnd / slides.length) * 100}%`;
      }

      if (nav) nav.style.display = '';
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= maxIndex;
    } else {
      index = 0;
      track.style.transform = '';
      if (nav) nav.style.display = 'none';
    }

    equalizeCardHeaders(panel);
  }

  function goTo(newIndex: number): void {
    if (!isCarouselMode()) return;

    const { maxIndex } = getCarouselMetrics();
    index = Math.max(0, Math.min(maxIndex, newIndex));
    update();
  }

  prev?.addEventListener('click', () => goTo(index - 1));
  next?.addEventListener('click', () => goTo(index + 1));
  window.addEventListener('resize', update);

  initCarouselSwipe({
    viewport,
    isEnabled: isCarouselMode,
    getBaseOffset,
    onDrag: applyTrackOffset,
    onCommit: (direction) => {
      if (!isCarouselMode()) return;

      if (direction === 0) {
        update();
        return;
      }

      goTo(index + direction);
    },
  });

  if (typeof window.ResizeObserver !== 'undefined') {
    const resizeObserver = new window.ResizeObserver(() => equalizeCardHeaders(panel));
    resizeObserver.observe(track);
  }

  slides.forEach((slide) => {
    slide.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => equalizeCardHeaders(panel), { once: true });
      }
    });
  });

  update();
}
